// 119 — Transferencia (Anexo 1 MSP, SNS-MSP/HCU-form.119/2021). Fase 7, Tanda 13: bloques A-O del papel (ambulancia,
// datos generales, diagnósticos, transferencia con origen/destino, equipos, constantes vitales en 5 tomas, procedimientos,
// descargo de medicamentos, novedades, equipo de transporte, centro regulador, tiempos, transporte aéreo y destino).
// Campos y decisiones en `bloques119Datos.ts`. Se conserva el `Procedure` "Transferencia".
import type { JSX, ReactNode } from 'react';
import { useMedplum } from '@medplum/react-hooks';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import type { FormularioDiscretoProps } from './index';
import { ErrorGuardado } from './ErrorGuardado';
import { useGuardado } from './useGuardado';
import { LoteTransaccional } from './transaccionFhir';
import { AreaDictado } from './AreaDictado';
import { Bloque, CamposSimples } from './camposSimples';
import type { CampoSimple, Valores } from './camposSimplesDatos';
import { SelectorDiagnosticoCie10 } from './SelectorDiagnosticoCie10';
import { TablaConColumnas, type ColumnaTablaConColumnas, type EstadoTablaConColumnas, type FilaTablaConColumnas } from './uci/TablaConColumnas';
import { EncabezadoMspCampos, valoresInicialesEncabezadoMsp } from '../EncabezadoMspCampos';
import { useDatosEncabezadoMsp } from '../useDatosEncabezadoMsp';
import { reasonReferenceDeCondicion } from '../recetas/reasonReference';
import { observacionesDeTomas } from './signosVitalesToma';
import {
  CAMPOS_AERONAVE_119,
  CAMPOS_AMBULANCIA_119,
  CAMPOS_DESTINO_119,
  CAMPOS_DESTINO_PACIENTE_119,
  CAMPOS_EQUIPOS_119,
  CAMPOS_GENERALES_119,
  CAMPOS_NOVEDADES_119,
  CAMPOS_OBSERVACIONES_AEREA_119,
  CAMPOS_OBSERVACIONES_EQUIPO_119,
  CAMPOS_OBSERVACIONES_REGULADOR_119,
  CAMPOS_ORIGEN_119,
  CAMPOS_PROCEDIMIENTOS_119,
  CAMPOS_TRANSFERENCIA_119,
  CAMPOS_TRANSPORTE_119,
  COLUMNAS_AEREA_119,
  COLUMNAS_CONSTANTES_119,
  COLUMNAS_DESCARGO_119,
  COLUMNAS_EQUIPO_119,
  COLUMNAS_REGULADOR_119,
  estadoInicial119,
  FILAS_AEREA_119,
  FILAS_CONSTANTES_119,
  FILAS_DESCARGO_119,
  FILAS_EQUIPO_119,
  FILAS_REGULADOR_119,
  motivosMarcados,
  paresDe119,
  tomasDe119,
  type Estado119,
} from './bloques119Datos';

type Clave = { [K in keyof Estado119]: Estado119[K] extends Valores ? K : never }[keyof Estado119];
type ClaveTabla = { [K in keyof Estado119]: Estado119[K] extends EstadoTablaConColumnas ? K : never }[keyof Estado119];

export function Formulario119({ subject, encounter, conditionId, onGuardado }: FormularioDiscretoProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const [estado, setEstado] = useState<Estado119>(() => estadoInicial119());
  const [campos, setCampos] = useState(() => valoresInicialesEncabezadoMsp());
  const { guardando, error, guardar } = useGuardado();
  // Servicio del encabezado del PDF (no el 'Consulta externa' por defecto): Transferencia en ambulancia: el papel no trae servicio; origen/destino no son un servicio de consulta.
  const datosEncabezadoMsp = useDatosEncabezadoMsp(subject, campos, '119', t('formularios.f119.titulo'), { sinServicio: true });

  /** Campos simples (casillas, selects, fechas...) más las áreas de texto con dictado de un bloque de `Estado119`. */
  const bloque = (clave: Clave, prefijo: string, lista: readonly CampoSimple[], columnas: 2 | 3 | 4, filas = 3): JSX.Element => {
    const valores: Valores = estado[clave];
    const poner = (v: Valores): void => setEstado({ ...estado, [clave]: v });
    const simples = lista.filter((c) => c.tipo !== 'area');
    return (
      <>
        {simples.length > 0 && <CamposSimples prefijoId={`f119-${prefijo}`} campos={simples} valores={valores} columnas={columnas} onChange={(v) => poner({ ...valores, ...v })} />}
        {lista
          .filter((c) => c.tipo === 'area')
          .map((c) => (
            <AreaDictado key={c.clave} id={`f119-${prefijo}-${c.clave}`} etiqueta={c.etiqueta} valor={valores[c.clave] ?? ''} filas={filas} onChange={(v) => poner({ ...valores, [c.clave]: v })} />
          ))}
      </>
    );
  };
  const tabla = (clave: ClaveTabla, f: readonly FilaTablaConColumnas[], c: readonly ColumnaTablaConColumnas[]): JSX.Element => (
    <TablaConColumnas filas={f} columnas={c} estado={estado[clave]} onChange={(e) => setEstado({ ...estado, [clave]: e })} />
  );
  const sub = (titulo: string, contenido: ReactNode): JSX.Element => (
    <div className="flex flex-col gap-2">
      <p className="text-sm font-medium text-hce-ink">{titulo}</p>
      {contenido}
    </div>
  );

  const handleSubmit = (): Promise<void> =>
    guardar(async () => {
      const lote = new LoteTransaccional();
      const notaTraslado = [
        estado.origen.origenNombre?.trim() && `Origen: ${estado.origen.origenNombre.trim()}`,
        estado.destino.destinoNombre?.trim() && `Destino: ${estado.destino.destinoNombre.trim()}`,
      ]
        .filter(Boolean)
        .join(' — ');
      const motivos = motivosMarcados(estado.transferencia);
      lote.crear({
        resourceType: 'Procedure',
        status: 'completed',
        code: { text: 'Transferencia' },
        subject,
        encounter,
        performedDateTime: new Date().toISOString(),
        note: notaTraslado ? [{ text: notaTraslado }] : undefined,
        reasonCode: motivos.length ? [{ text: motivos.join(', ') }] : undefined,
        reasonReference: reasonReferenceDeCondicion(conditionId),
      });
      const ahora = new Date().toISOString();
      observacionesDeTomas(tomasDe119(estado), subject, encounter, ahora).forEach((o) => lote.crear(o));
      await lote.enviar(medplum);
      onGuardado(paresDe119(estado), datosEncabezadoMsp);
    });

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-base font-medium text-hce-ink">{t('formularios.f119.titulo')}</h3>

      <EncabezadoMspCampos subject={subject} value={campos} onChange={setCampos} />

      <Bloque titulo={t('formularios.f119.ambulanciaTitulo')}>{bloque('ambulancia', 'amb', CAMPOS_AMBULANCIA_119, 3)}</Bloque>
      <Bloque titulo={t('formularios.f119.generalesTitulo')}>{bloque('generales', 'gen', CAMPOS_GENERALES_119, 4)}</Bloque>

      <Bloque titulo={t('formularios.f119.diagnosticosTitulo')}>
        {estado.diagnosticos.map((d, i) => (
          <SelectorDiagnosticoCie10 key={i} label={`${t('formularios.f119.diagnostico')} ${i + 1}`} value={d} onChange={(v) => setEstado({ ...estado, diagnosticos: estado.diagnosticos.map((x, j) => (j === i ? v : x)) })} />
        ))}
      </Bloque>

      <Bloque titulo={t('formularios.f119.transferenciaTitulo')}>
        {bloque('transferencia', 'tra', CAMPOS_TRANSFERENCIA_119, 4)}
        {sub(t('formularios.f119.origen'), bloque('origen', 'ori', CAMPOS_ORIGEN_119, 3))}
        {sub(t('formularios.f119.destino'), bloque('destino', 'des', CAMPOS_DESTINO_119, 3))}
      </Bloque>

      <Bloque titulo={t('formularios.f119.equiposTitulo')}>{bloque('equipos', 'equ', CAMPOS_EQUIPOS_119, 4)}</Bloque>

      <Bloque titulo={t('formularios.f119.constantesTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f119.constantesNota')}</p>
        {tabla('constantes', FILAS_CONSTANTES_119, COLUMNAS_CONSTANTES_119)}
      </Bloque>

      <Bloque titulo={t('formularios.f119.procedimientosTitulo')}>{bloque('procedimientos', 'pro', CAMPOS_PROCEDIMIENTOS_119, 2, 6)}</Bloque>

      <Bloque titulo={t('formularios.f119.descargoTitulo')}>
        <p className="text-xs text-hce-muted">{t('formularios.f119.descargoNota')}</p>
        {tabla('descargo', FILAS_DESCARGO_119, COLUMNAS_DESCARGO_119)}
      </Bloque>

      <Bloque titulo={t('formularios.f119.novedadesTitulo')}>{bloque('novedades', 'nov', CAMPOS_NOVEDADES_119, 2, 6)}</Bloque>

      <Bloque titulo={t('formularios.f119.equipoTitulo')}>
        {tabla('equipoTransporte', FILAS_EQUIPO_119, COLUMNAS_EQUIPO_119)}
        {bloque('obsEquipo', 'oeq', CAMPOS_OBSERVACIONES_EQUIPO_119, 2)}
      </Bloque>

      <Bloque titulo={t('formularios.f119.reguladorTitulo')}>
        {tabla('regulador', FILAS_REGULADOR_119, COLUMNAS_REGULADOR_119)}
        {bloque('obsRegulador', 'ore', CAMPOS_OBSERVACIONES_REGULADOR_119, 2)}
      </Bloque>

      <Bloque titulo={t('formularios.f119.transporteTitulo')}>{bloque('transporte', 'tpt', CAMPOS_TRANSPORTE_119, 3)}</Bloque>

      <details className="rounded-md border border-border p-3">
        <summary className="cursor-pointer text-sm font-medium text-hce-ink">{t('formularios.f119.aereoTitulo')}</summary>
        <div className="mt-3 flex flex-col gap-4">
          {sub(t('formularios.f119.tripulacionTitulo'), tabla('aerea', FILAS_AEREA_119, COLUMNAS_AEREA_119))}
          {bloque('obsAerea', 'oae', CAMPOS_OBSERVACIONES_AEREA_119, 2)}
          {sub(t('formularios.f119.aeronaveTitulo'), bloque('aeronave', 'aer', CAMPOS_AERONAVE_119, 3))}
        </div>
      </details>

      <Bloque titulo={t('formularios.f119.destinoPacienteTitulo')}>{bloque('destinoPaciente', 'dpa', CAMPOS_DESTINO_PACIENTE_119, 3)}</Bloque>

      <ErrorGuardado error={error} />

      <Button type="button" className="w-fit" onClick={() => void handleSubmit()} disabled={guardando}>
        {t('formulario.guardar')}
      </Button>
    </div>
  );
}
